import { inject, Injectable } from '@angular/core';
import { IConfigData } from '@df/catalog/config//config.interfaces';
import { TLoadParams } from '@df/catalog/model';
import { ICmsContentService } from '@icms/core/content/icms-content.service';
import { Nexus } from '@tomandco/nexus-api-types';
import { getBlockUrl } from '../block/get-block-url';
import { ICmsModelResource } from '../model/icms-model.resource';
import { ICmsConfig } from './icms-config';
interface IRetrieveRequest {
    bareUrl: string;
    variant?: string;
}

@Injectable({
    providedIn: 'root'
})
export class ICmsConfigResource extends ICmsModelResource<ICmsConfig, number> {
    static override instance = ICmsConfig;
    private iCmsContentService = inject(ICmsContentService);
    readonly invalidatesUniversal = true;
    readonly invalidatesPrerender = false;

    /**
     * Loads item by entry id
     */
    loadModelByUrl<TContent = any>(
        entryId: string,
        params: Partial<TLoadParams> = { safe: true }
    ): Promise<ICmsConfig<IConfigData<TContent>> | undefined> {
        return this.findOne({ filter: { url: entryId }, verbosity: 1 }, params);
    }

    /**
     * Finds config by its url
     */
    getModelByUrl<TContent = any>(entryId: string): ICmsConfig<IConfigData<TContent>> | undefined {
        return this.getAllExisting().find(i => i.data?.url === entryId);
    }

    /**
     * Loads countries list (passed from magento)
     */
    loadCountries(): Promise<ICmsConfig<IConfigData<Nexus.Lib.General.Country[]>> | undefined> {
        return this.loadModelByUrl('appCountries');
    }

    async retrieve<TContent = any>(
        request: IRetrieveRequest,
        params: Partial<TLoadParams> | undefined // keep it mandatory, its importatnt to pass params when possible mainly for loader
    ): Promise<ICmsConfig<IConfigData<TContent>> | undefined> {
        const url = getBlockUrl(request.bareUrl, request.variant);

        try {
            return await this.loadModelByUrl(url, { ...params, safe: false });
        } catch (err) {
            return this.init(request.bareUrl, request.variant);
        }
    }

    init<TContent = any>(bareUrl: string, variant?: string): ICmsConfig {
        const url = getBlockUrl(bareUrl, variant);
        const model = this.getModelByUrl(url) || this.getCreate(Math.min(0, ...this.getAll().map(i => i.id)) - 1);
        model.setData({
            id: model.id,
            url,
            type: ICmsConfig.type,
            verbosity: 1,
            content: this.iCmsContentService.initFieldset<TContent, undefined>(
                this.iCmsContentService.getConfig(bareUrl)?.fields || [],
                undefined,
                {},
                this.iCmsContentService.getConfig(bareUrl)?.initialData
            )
        });
        return model;
    }
}
